'use client';
import React, { useState } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { useToast } from '@/context/ToastContext';

interface InquiryTemplateProps {
  page: any;
  meta: any;
  slug: string;
}

export default function InquiryTemplate({ page, meta }: InquiryTemplateProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    inquiryType: 'buy',
    propertyType: 'residential',
    budget: '',
    message: '',
    agree: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.mobile ||
      !formData.agree
    ) {
      toast.error('Please fill out all mandatory fields and accept the Privacy Policy.');
      return;
    }

    setLoading(true);
    try {
      await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'inquiry',
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.mobile,
          details: {
            inquiryType: formData.inquiryType,
            propertyType: formData.propertyType,
            budget: formData.budget,
            message: formData.message,
          },
        }),
      });
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Jost, sans-serif' }}>
      <Header />

      {/* Hero Banner */}
      <div className="relative w-full h-[60vh] flex items-center justify-center pt-[100px] overflow-hidden bg-slate-950">
        <div className="absolute inset-0">
          <img
            src={page.featured_image || '/uploads/Plotae-Web-Banner-1200x900-1.jpg'}
            alt={page.title}
            className="w-full h-full object-cover transform scale-105 transition-transform duration-[8000ms] ease-out opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight leading-none drop-shadow-md">
            {page.title}
          </h1>
          <p className="text-sm md:text-lg text-slate-200 mt-6 tracking-widest uppercase font-bold max-w-2xl mx-auto leading-relaxed">
            {meta?.banner_subtitle ||
              'Speak with our specialist team today for personalized guidance'}
          </p>
          <div className="w-20 h-0.5 bg-[#006838] mx-auto mt-8"></div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="py-20 bg-slate-50/50">
        <div className="container mx-auto px-6" style={{ maxWidth: '1280px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Content Column */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-[120px]">
              <div>
                <span className="text-[11px] font-black text-[#006838] uppercase tracking-[0.25em]">
                  PlotAE Investments
                </span>
                <h2 className="text-3xl lg:text-4xl font-extrabold text-[#1a1a1a] mt-2 mb-6 leading-tight uppercase tracking-tight">
                  Connect With Our Land Experts
                </h2>
                <div className="w-16 h-1 bg-[#006838] mb-6 rounded-full" />
              </div>

              <p className="text-slate-600 text-[15px] leading-relaxed font-medium">
                Whether you are looking to acquire a prime development plot, sell an existing land
                asset, or gain expert valuation intelligence, our dedicated team of advisors is here
                to support you at every stage.
              </p>

              <div className="space-y-4 pt-4 border-t border-slate-200/80">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#006838]/10 flex items-center justify-center text-[#006838]">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest leading-none mb-1">
                      Direct Helpline
                    </p>
                    <a
                      href="tel:+971501120273"
                      className="text-slate-900 font-extrabold text-sm hover:text-[#006838] transition-colors"
                    >
                      +971 50 112 0273
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#006838]/10 flex items-center justify-center text-[#006838]">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest leading-none mb-1">
                      Email Inquiries
                    </p>
                    <a
                      href="mailto:info@plotae.com"
                      className="text-slate-900 font-extrabold text-sm hover:text-[#006838] transition-colors"
                    >
                      info@plotae.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-slate-200/60 p-8 lg:p-10 shadow-xl shadow-slate-100/50 rounded-none relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#006838]"></div>

                {submitted ? (
                  <div className="py-12 text-center space-y-6 animate-in fade-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto text-[#006838] border border-green-100 shadow-sm">
                      <svg
                        className="w-10 h-10 animate-bounce"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tight uppercase">
                      Inquiry Captured
                    </h3>
                    <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                      Thank you for contacting Plotae. One of our senior land advisors has been
                      assigned to your request and will connect with you within 2 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-3 bg-[#006838] hover:bg-[#00502b] text-white text-xs font-black uppercase tracking-widest transition-colors duration-200 rounded-none shadow-md shadow-green-900/10"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <h3 className="text-lg font-black text-slate-900 tracking-tight uppercase leading-none">
                        Share Your Requirements
                      </h3>
                      <p className="text-xs text-slate-400 mt-1 font-medium">
                        Please provide your details below and we will contact you shortly.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          * First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="e.g. John"
                          className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm transition-all"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          * Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="e.g. Doe"
                          className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          * Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john.doe@company.com"
                          className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm transition-all"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          * Mobile Phone
                        </label>
                        <input
                          type="tel"
                          name="mobile"
                          required
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="+971 50 123 4567"
                          className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          Inquiry Type
                        </label>
                        <select
                          name="inquiryType"
                          value={formData.inquiryType}
                          onChange={handleChange}
                          className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm cursor-pointer transition-all uppercase tracking-wider font-semibold"
                        >
                          <option value="buy">Acquisition / Buy</option>
                          <option value="sell">Disposition / Sell</option>
                          <option value="valuation">Plot Valuation</option>
                          <option value="consultancy">Consultancy</option>
                        </select>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          Zoning / Use Type
                        </label>
                        <select
                          name="propertyType"
                          value={formData.propertyType}
                          onChange={handleChange}
                          className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm cursor-pointer transition-all uppercase tracking-wider font-semibold"
                        >
                          <option value="residential">Residential Plot</option>
                          <option value="commercial">Commercial Plot</option>
                          <option value="mixed-use">Mixed-Use Plot</option>
                          <option value="industrial">Industrial Plot</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                        Budget Range (AED)
                      </label>
                      <input
                        type="text"
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        placeholder="e.g. 5,000,000"
                        className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm transition-all"
                      />
                    </div>

                    <div className="flex flex-col">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                        Message / Details
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Please write down any specific locations, plot parameters, or expectations you have..."
                        className="px-4 py-3 bg-slate-50/50 border border-slate-200 focus:bg-white focus:ring-1 focus:ring-[#006838] focus:border-[#006838] outline-none text-sm resize-none transition-all"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        name="agree"
                        id="agree-checkbox"
                        checked={formData.agree}
                        onChange={handleChange}
                        className="mt-1 w-4.5 h-4.5 accent-[#006838] cursor-pointer"
                      />
                      <label
                        htmlFor="agree-checkbox"
                        className="text-xs text-slate-500 leading-relaxed cursor-pointer font-medium select-none"
                      >
                        I agree to receive communications from PlotAE and accept the privacy policy.
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-[#006838] hover:bg-[#00502b] disabled:bg-slate-300 text-white text-xs font-black uppercase tracking-widest transition-colors duration-200 rounded-none shadow-lg shadow-green-900/10 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Capturing...</span>
                        </>
                      ) : (
                        <span>Submit Inquiry</span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
